/**
 * Google OAuth Service
 * Handles Google OAuth 2.0 authentication flow
 */

import tokenManager from "../api/token-manager";
import { TokenResponse } from "../../types/api";
import { isCapacitor } from "../../lib/platform";
import { getNativeRedirectUri, openOAuthUrl } from "./native-oauth";

const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  "1051383338234-2h89tvqn6vkiuo38c3q8n3n4q1v1qjrm.apps.googleusercontent.com";
const WEB_REDIRECT_URI =
  import.meta.env.VITE_GOOGLE_REDIRECT_URI ||
  "http://localhost:3000/auth/google/callback";

export class GoogleOAuthService {
  /**
   * Get the appropriate redirect URI based on platform
   */
  static getRedirectUri(): string {
    if (isCapacitor()) {
      return getNativeRedirectUri("google");
    }
    return WEB_REDIRECT_URI;
  }

  /**
   * Initiate Google OAuth login
   * On web: redirects to Google consent screen
   * On native: opens in-app browser with custom scheme redirect
   */
  static async initiateLogin(): Promise<void> {
    const redirectUri = this.getRedirectUri();
    const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");

    authUrl.searchParams.set("client_id", GOOGLE_CLIENT_ID);
    authUrl.searchParams.set("redirect_uri", redirectUri);
    authUrl.searchParams.set("response_type", "id_token token");
    authUrl.searchParams.set("scope", "openid email profile");
    authUrl.searchParams.set("nonce", this.generateNonce());
    authUrl.searchParams.set("state", this.generateState());

    // Store state in localStorage for validation (more reliable than sessionStorage)
    const state = authUrl.searchParams.get("state");
    if (state) {
      localStorage.setItem("oauth_state", state);
    }

    // On native, open in-app browser; on web, redirect
    const handled = await openOAuthUrl(authUrl.toString());
    if (!handled) {
      window.location.href = authUrl.toString();
    }
  }

  /**
   * Handle OAuth callback and exchange ID token for JWT
   * Uses same backend endpoint as Chrome extension (/auth/google/token)
   * @param hashParams URL hash parameters from redirect
   */
  static async handleCallback(
    hashParams: URLSearchParams,
  ): Promise<TokenResponse> {
    // Extract tokens from hash
    const idToken = hashParams.get("id_token");
    const accessToken = hashParams.get("access_token");
    const state = hashParams.get("state");

    if (!idToken || !accessToken) {
      throw new Error("Missing tokens in OAuth callback");
    }

    // Validate state (skip if state is missing from both sides - some OAuth flows)
    const storedState = localStorage.getItem("oauth_state");

    if (state && storedState && state !== storedState) {
      console.error("State mismatch! Received:", state, "Stored:", storedState);
      throw new Error("Invalid OAuth state");
    }

    // Clean up stored state
    localStorage.removeItem("oauth_state");

    // Fetch user info from Google API (same as extension)
    const userInfoResponse = await fetch(
      "https://www.googleapis.com/oauth2/v2/userinfo",
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );

    if (!userInfoResponse.ok) {
      throw new Error(`Failed to fetch user info: ${userInfoResponse.status}`);
    }

    const userInfo = await userInfoResponse.json();

    // Exchange Google ID token for backend JWT
    // Using same format as Chrome extension: ID token in Authorization header
    const API_URL =
      import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";
    const response = await fetch(`${API_URL}/auth/google/token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
        "X-App-Source": "react",
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Authentication failed" }));
      console.error("Backend OAuth error response:", error);
      console.error("Response status:", response.status);
      throw new Error(
        error.message ||
          error.error ||
          `HTTP ${response.status}: Authentication failed`,
      );
    }

    const data = await response.json();

    if (!data.token) {
      console.error("No token in response. Full response:", data);
      throw new Error("No JWT token received from server");
    }

    // Use Google API user info instead of backend response
    // This ensures we always have fresh, accurate user data
    const userData = {
      id: data.user?.id || userInfo.id,
      email: userInfo.email,
      name: userInfo.name,
      picture: userInfo.picture,
    };

    // Store JWT token, OAuth access token, and user data
    tokenManager.setTokens({
      accessToken: data.token,
      provider: "google",
      user: userData,
      oauthAccessToken: accessToken,
      rawUserInfo: userInfo,
      googleIdToken: idToken, // <-- ADD THIS LINE
    });

    console.log(
      "Token and user data stored. User:",
      userData.name,
      userData.email,
    );
    console.log(
      "Stored in localStorage: jwt_token, user_data, googleAccessToken, googleUserInfo",
    );

    return {
      jwt_token: data.token,
      user: userData,
    } as TokenResponse;
  }

  /**
   * Generate random nonce for OAuth security
   */
  private static generateNonce(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  static async syncFromExtension(
    idToken: string,
    accessToken: string,
    userInfo: any,
  ): Promise<TokenResponse> {
    const API_URL =
      import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";

    console.log("Exchanging synced extension token for Web JWT...");

    // 1. Fetch a new JWT specifically for the React web app
    const response = await fetch(`${API_URL}/auth/google/token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
        "X-App-Source": "react", // <-- Forces a "react" fingerprint
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Sync failed" }));
      throw new Error(error.message || error.error || "Authentication failed");
    }

    const data = await response.json();

    const userData = {
      id: data.user?.id || userInfo.id || "",
      email: userInfo.email,
      name: userInfo.name,
      picture: userInfo.picture || userInfo.profileImage,
    };

    // 2. Use tokenManager to properly set everything up in the web app ecosystem
    tokenManager.setTokens({
      accessToken: data.token,
      provider: "google",
      user: userData,
      oauthAccessToken: accessToken,
      rawUserInfo: userInfo,
      googleIdToken: idToken,
    });

    return {
      jwt_token: data.token,
      user: userData,
    } as TokenResponse;
  }

  /**
   * Generate random state for CSRF protection
   */
  private static generateState(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
}

export default GoogleOAuthService;
